import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { Mesh } from 'three';
import { MountingPoint } from '../../types/site';
import { useSiteStore } from '../../store/siteStore';

interface MountingPointsProps {
  mountingPoints: MountingPoint[];
}

const MountingPoints: React.FC<MountingPointsProps> = ({ mountingPoints }) => {
  const { setSelectedMountingPoint, setEquipmentModalOpen } = useSiteStore();

  return (
    <group>
      {mountingPoints.map((point) => {
        const radius = 0.15; // meters
        const angle = (point.angle * Math.PI) / 180;
        const towerRadius = 0.3; // meters from center
        const x = Math.cos(angle) * towerRadius;
        const z = Math.sin(angle) * towerRadius;
        const y = point.height;

        return (
          <MountingPointSphere
            key={point.id}
            position={[x, y, z]}
            occupied={point.occupied}
            height={point.height}
            side={point.side}
            onClick={() => {
              if (!point.occupied) {
                setSelectedMountingPoint(point);
                setEquipmentModalOpen(true);
              }
            }}
          />
        );
      })}
    </group>
  );
};

interface MountingPointSphereProps {
  position: [number, number, number];
  occupied: boolean;
  height: number;
  side: string;
  onClick: () => void;
}

const MountingPointSphere: React.FC<MountingPointSphereProps> = ({
  position,
  occupied,
  height,
  side,
  onClick
}) => {
  const meshRef = useRef<Mesh>(null);
  const [hovered, setHovered] = React.useState(false);

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.scale.setScalar(hovered ? 1.5 : 1);
    }
  });

  const handlePointerOver = (e: any) => {
    e.stopPropagation();
    setHovered(true);
    if (!occupied) {
      document.body.style.cursor = 'pointer';
    }
  };

  const handlePointerOut = () => {
    setHovered(false);
    document.body.style.cursor = 'default';
  };

  const color = occupied ? '#ef4444' : hovered ? '#3b82f6' : '#9ca3af';
  const opacity = occupied ? 0.8 : hovered ? 0.6 : 0.3;

  const handleClick = (e: any) => {
    e.stopPropagation();
    if (!occupied) {
      onClick();
    }
  };

  const handlePointerDown = (e: any) => {
    // Only handle left mouse button clicks
    if (e.button === 0 && !occupied) {
      e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
      onClick();
    }
  };

  return (
    <group position={position}>
      {/* Invisible larger hitbox for easier clicking (only for unoccupied points) - render first so it's on top */}
      {!occupied && (
        <mesh
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
          renderOrder={1000}
        >
          <sphereGeometry args={[0.2, 16, 16]} />
          <meshStandardMaterial 
            visible={false}
            depthWrite={false}
            depthTest={true}
          />
        </mesh>
      )}
      
      {/* Visible sphere */}
      <mesh
        ref={meshRef}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        renderOrder={999}
      >
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={opacity}
          emissive={hovered ? color : '#000000'}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      
      {/* Hover tooltip - positioned above to not block the mounting point */}
      {hovered && (
        <Html position={[0, 0.25, 0]} center>
          <div className="bg-white/80 text-gray-700 text-xs px-2 py-1 rounded whitespace-nowrap pointer-events-none shadow-md border border-gray-200">
            {height}m • {side}
          </div>
        </Html>
      )}
    </group>
  );
};

export default React.memo(MountingPoints);

